import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { fadeUp, stagger, viewport } from '../../utils/animations';

const FACTS = [
  { value: 4,  suffix: '+', label: 'Years of working',  desc: 'experience in building production-grade web and mobile applications',   color: '#6366f1', icon: '⏱️' },
  { value: 50, suffix: '+', label: 'Projects delivered',   desc: 'Across 10+ industries',        color: '#8b5cf6', icon: '🚀' },
  { value: 50, suffix: '+', label: 'Happy clients',        desc: 'Global reach, high retention', color: '#06b6d4', icon: '🤝' },
  { value: 2,  suffix: '',  label: 'Countries worked in',  desc: 'Australia & Singapore',        color: '#10b981', icon: '🌏' },
];

function Counter({ target, suffix, running }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!running) return;
    let start = 0;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 20);
    return () => clearInterval(timer);
  }, [running, target]);

  return <>{count}{suffix}</>;
}

export default function Facts() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="facts" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-white/[0.015] border-y border-white/[0.05]" />
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 80% 100% at 50% 50%, rgba(99,102,241,0.05) 0%, transparent 70%)' }} />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8" ref={ref}>
        <motion.div
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="grid grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {FACTS.map((f, i) => (
            <motion.div
              key={f.label}
              variants={fadeUp}
              whileHover={{ y: -6, scale: 1.02 }}
              transition={{ duration: 0.25 }}
              className="group relative glass glass-hover rounded-3xl p-6 sm:p-8 border border-white/[0.06] overflow-hidden text-center"
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(ellipse 70% 70% at 50% 80%, ${f.color}12, transparent)` }}
              />

              <span className="text-3xl mb-3 block">{f.icon}</span>
              <div
                className="font-display font-extrabold text-4xl sm:text-5xl leading-none mb-2"
                style={{ color: f.color }}
              >
                <Counter target={f.value} suffix={f.suffix} running={inView} />
              </div>
              <p className="font-semibold text-sm text-white mb-1">{f.label}</p>
              <p className="text-xs text-zinc-600">{f.desc}</p>

              <motion.div
                className="absolute bottom-0 left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `linear-gradient(90deg, transparent, ${f.color}, transparent)` }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
